import React from 'react';
import {useParams} from 'react-router-dom';
import {IconName} from '@lightricks/react-design-system';
import raiseFlashMessage, {
  raiseFlashMessageError,
} from '@/utils/raiseFlashMessage';
import queryClient from '@/utils/reactQueryClient';
import translate from '@/utils/translate';
import PERMISSIONS from '@/config/permissions';
import {
  CREATOR_ACTION_CONFIG,
  isActionVisibleForStatus,
} from '@/views/campaigns/creators-tab/types/creatorActionConfig';
import {CREATOR_STATUS} from '@/views/campaigns/creators-tab/types/creatorTabTypes';
import type {
  CampaignCreatorApiResponse,
  CampaignCreatorCounts,
  CampaignCreatorRow,
  CreatorStatus,
} from '@/views/campaigns/creators-tab/types/creatorTabTypes';
import updateCreatorCounts from '@/views/campaigns/creators-tab/utils/updateCreatorCounts';
import {
  CREATOR_ACTION_KEYS,
  type CreatorActionKey,
} from '@/views/creators/components/creator-action/CreatorAction';
import {ActionItem} from '@/views/creators/components/creators-actions-bar/ActionsBar';
import FlashMessage from '@/components/flash-message';
import useBatchCreateVetting from '@/hooks/mutations/brand-safety/use-batch-create-vetting';
import useBulkProposalAction from '@/hooks/mutations/use-bulk-proposal-action';
import useShortlist from '@/hooks/mutations/use-shortlist-update';
import useBrandId from '@/hooks/use-brand-id';
import useDashboardUserPermissions from '@/hooks/use-dashboard-user-permissions';

interface UseBulkCreatorActionsParams {
  selectedCreators: CampaignCreatorRow[];
  activeStatus: CreatorStatus;
  onSetActiveAction: (action: CreatorActionKey) => void;
  onChangeDueDate: () => void;
  onMessage: () => void;
  onDone: () => void;
  onActionComplete: (countAdjustment?: Partial<CampaignCreatorCounts>) => void;
}

function useBulkCreatorActions({
  selectedCreators,
  activeStatus,
  onSetActiveAction,
  onChangeDueDate,
  onMessage,
  onDone,
  onActionComplete,
}: UseBulkCreatorActionsParams): ActionItem[] {
  const {campaignId} = useParams<{campaignId: string}>();
  const brandId = useBrandId();
  const contractPermissions = useDashboardUserPermissions([
    PERMISSIONS.CONTRACT.UPDATE,
  ]);
  const bulkProposalAction = useBulkProposalAction();
  const batchCreateVetting = useBatchCreateVetting({brandId});
  const shortlist = useShortlist(campaignId, selectedCreators);

  const selectedIds = selectedCreators.map((c) => c.id);
  // If every selected creator is already shortlisted, the bulk action becomes
  // a remove. Otherwise it adds (filling in any that aren't yet shortlisted).
  const allSelectedShortlisted = shortlist.areAllShortlisted(selectedIds);

  function optimisticallyRemoveCreators(creators: CampaignCreatorRow[]) {
    const paidGigIds = new Set(creators.map((c) => c.paidGigId));

    queryClient.setQueriesData<{
      pages?: {data: CampaignCreatorApiResponse[]}[];
      pageParams?: unknown[];
    }>(
      {queryKey: ['campaignV2Creators', {campaignId}], exact: false},
      (old) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            data: page.data.filter((c) => !paidGigIds.has(c.id)),
          })),
        };
      }
    );
  }

  function bulkProposalHandler(
    action: 'accept' | 'reject',
    countAdjustment: Partial<CampaignCreatorCounts>
  ) {
    const eligible = selectedCreators.filter(
      (c) => c.paidGigId && c.applicantStatus === CREATOR_STATUS.Applications
    );
    if (eligible.length === 0) return;

    optimisticallyRemoveCreators(eligible);
    updateCreatorCounts(campaignId, brandId, countAdjustment);

    bulkProposalAction.mutate(
      {
        action,
        paidGigIds: eligible.map((c) => c.paidGigId as string),
      },
      {
        onSuccess: () => onActionComplete(),
        onError: () => onActionComplete(),
      }
    );
    onDone();
  }

  const eligibleCount = selectedCreators.filter(
    (c) => c.paidGigId && c.applicantStatus === CREATOR_STATUS.Applications
  ).length;

  const handlers: Record<string, () => void> = {
    'accept': () =>
      bulkProposalHandler('accept', {
        applications: -eligibleCount,
        hired: eligibleCount,
      }),
    'reject': () =>
      bulkProposalHandler('reject', {
        applications: -eligibleCount,
        declined: eligibleCount,
      }),
    'vet': () => {
      const eligible = selectedCreators.filter((c) => !c.vettingId);
      if (eligible.length === 0) return;
      batchCreateVetting.mutate(
        {profileIds: eligible.map((c) => c.profileId), brandId},
        {
          onSuccess: (result) => {
            raiseFlashMessage({
              status: 'success',
              mode: 'light',
              message: (
                <FlashMessage.Banner
                  title={translate('Vetting started')}
                  subtitle={translate('{{count}} creators', {
                    count: result.success.length,
                  })}
                />
              ),
            });
          },
          onError: () => {
            raiseFlashMessageError();
          },
        }
      );
      onDone();
    },
    'message': () => {
      // Only contracted creators (those with a paid gig) can be messaged in
      // bulk; the modal posts their paidGigIds as `contract_ids`.
      const hasEligibleContracts = selectedCreators.some((c) => c.paidGigId);
      if (!hasEligibleContracts) {
        return;
      }
      onMessage();
    },
    'add-to-list': () => {
      onSetActiveAction(CREATOR_ACTION_KEYS.ADD_TO_MY_CREATORS);
    },
    'share': () => {
      onSetActiveAction(CREATOR_ACTION_KEYS.CREATE_AND_SHARE_LIST);
    },
    'add-to-shortlist': () => {
      if (selectedIds.length === 0) return;
      const subtitle =
        selectedCreators.length === 1
          ? selectedCreators[0].creatorName
          : translate('{count} creators', {count: selectedIds.length});
      if (allSelectedShortlisted) {
        shortlist.removeMany(selectedIds);
        raiseFlashMessage({
          status: 'success',
          mode: 'light',
          message: (
            <FlashMessage.Banner
              title={translate('Removed from shortlist')}
              subtitle={subtitle}
            />
          ),
        });
      } else {
        shortlist.addMany(selectedIds);
        raiseFlashMessage({
          status: 'success',
          mode: 'light',
          message: (
            <FlashMessage.Banner
              title={translate('Added to shortlist')}
              subtitle={subtitle}
            />
          ),
        });
      }
      onDone();
    },
  };

  // Gate the bulk due-date action by the same Contract/update permission the
  // row menu uses. Omitting the handler hides the action via the filter below.
  if (contractPermissions.updateContract) {
    handlers['change-due-date'] = () => {
      const hasEligibleContracts = selectedCreators.some((c) => c.paidGigId);
      if (!hasEligibleContracts) {
        return;
      }
      // Opens the batch due-date modal; API errors are surfaced by the
      // modal's mutation onError, mirroring the other delegate actions.
      onChangeDueDate();
    };
  }

  return CREATOR_ACTION_CONFIG.filter(
    (action) =>
      action.id in handlers && isActionVisibleForStatus(action, activeStatus)
  ).map((action) => {
    // The shortlist bulk action is a smart toggle: when every selected creator
    // is already shortlisted, the icon flips to filled-heart and the tooltip
    // becomes a remove action. Otherwise it's the default add affordance.
    const isShortlistAction = action.id === 'add-to-shortlist';
    const icon: IconName =
      isShortlistAction && allSelectedShortlisted
        ? 'Navigation-Heart-Fill'
        : (action.icon as IconName);
    const tooltip =
      isShortlistAction && allSelectedShortlisted
        ? translate('Remove from shortlist')
        : translate(action.label);

    return {
      id: `bulk-${action.id}`,
      icon,
      tooltip,
      onClick: handlers[action.id],
    };
  });
}

export default useBulkCreatorActions;
